import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearSelectedEvent, fetchEventLight, fetchEvents, setFilters } from '../redux/slices/eventsSlice';
import { toggleShowStats } from '../redux/slices/uiSlice';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TabPanel from '../components/TabPanel';
import EventsDrawer, { EventsDrawerType } from '../components/EventsDrawer';
import EventsTable from '../components/EventsTable';
import OrdersDialog from '../components/orders/OrdersDialog';
import { DEFAULT_ROWS_PER_PAGE, DEFAULT_PAGE } from '../utils/paginationUtils';

function Events() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openCreate, setOpenCreate] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedOrderEvent, setSelectedOrderEvent] = useState(null);

  const eventsData = useSelector(({ events: { allEvents } }) => allEvents);
  const isLoading = useSelector((state) => state.events.isLoading);
  const eventsDataError = useSelector((state) => state.events.error);
  const options = useSelector((state) => state.events.options);
  const totalItems = useSelector((state) => state.events.totalItems);

  const showStats = useSelector((state) => state.ui.events.showStats);
  const toggleStats = () => dispatch(toggleShowStats());

  const user = useSelector((state) => state.auth.user);

  const handleOrdersClose = () => {
    setSelectedOrderEvent(null);
  };

  const handleDrawerClose = () => {
    setOpenCreate(false);
    setSelectedEvent(null);
    dispatch(clearSelectedEvent());
  };

  const onCopy = (eventId) => {
    dispatch(fetchEventLight({ eventId, salesChannel: 'all' }))
      .unwrap()
      .then((res) => {
        const { data: event } = res;
        setSelectedEvent(event);
        return event;
      })
      .catch((err) => {
        console.log(err);
        toast.error(t('An error occured please try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
      });
  };

  useEffect(() => {
    const filtersFromUrl = {
      page: parseInt(searchParams.get('page')) || DEFAULT_PAGE,
      limit: parseInt(searchParams.get('limit')) || DEFAULT_ROWS_PER_PAGE,
      selectedTabId: parseInt(searchParams.get('selectedTabId')) || 0,
    };
    dispatch(fetchEvents(filtersFromUrl));
    dispatch(setFilters(filtersFromUrl));
  }, [dispatch, searchParams]);

  useEffect(() => {
    setSearchParams(
      {
        page: options.page,
        limit: options.limit,
        selectedTabId: options.selectedTabId,
      },
      { replace: true }
    );
  }, [options.page, options.limit, options.selectedTabId, setSearchParams]);

  const updateFilters = (patch) => {
    const updated = { ...options, ...patch };
    dispatch(setFilters(updated));
    dispatch(fetchEvents(updated));
    setSearchParams(updated);
  };

  const handleChangePage = (_, newPage) => updateFilters({ page: newPage + 1 });
  const handleChangeRowsPerPage = (e) => updateFilters({ limit: +e.target.value, page: 1 });

  useEffect(() => {
    if (eventsDataError) {
      toast.error(t('An error occured, please refresh the page and try again!'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [eventsDataError, t]);

  return (
    <div className="events">
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h3">{t('Events')}</Typography>
        </Grid>
        <Grid item>
          {user.role === 'organizer' && (
            <Button variant="contained" onClick={() => setOpenCreate(true)}>
              {t('Add event')}
            </Button>
          )}
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={options.selectedTabId}
          onChange={(_, newValue) => {
            updateFilters({ selectedTabId: newValue });
            searchParams.set('selectedTabId', newValue);
            setSearchParams(searchParams, { replace: true });
          }}
        >
          <Tab label={t('Ongoing')} />
          <Tab label={t('Archived')} />
        </Tabs>
      </Box>
      <TabPanel disabled={isLoading} value={options.selectedTabId} index={0}>
        {user.role === 'cashier' && (
          <FormGroup sx={{ display: 'flex', alignItems: 'end', marginBottom: 2 }}>
            <FormControlLabel control={<Switch checked={showStats} onChange={toggleStats} />} label={t('Show stats')} />
          </FormGroup>
        )}
        <EventsTable
          setSelectedEvent={setSelectedOrderEvent}
          onCopy={onCopy}
          events={eventsData}
          {...(user.role === 'cashier' ? { showStats } : {})}
          pagination={{
            totalItems,
            page: options.page,
            limit: options.limit,
            onPageChange: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          }}
        />
      </TabPanel>
      <TabPanel disabled={isLoading} value={options.selectedTabId} index={1}>
        <EventsTable
          setSelectedEvent={setSelectedOrderEvent}
          onCopy={onCopy}
          events={eventsData}
          pagination={{
            totalItems,
            page: options.page,
            limit: options.limit,
            onPageChange: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          }}
        />
      </TabPanel>
      {user.role === 'organizer' && (
        <EventsDrawer
          type={openCreate ? EventsDrawerType.CREATE : EventsDrawerType.COPY}
          open={openCreate || !!selectedEvent}
          event={selectedEvent}
          onClose={handleDrawerClose}
        />
      )}
      {user.role === 'cashier' && (
        <OrdersDialog eventId={selectedOrderEvent} open={!!selectedOrderEvent} onClose={handleOrdersClose} />
      )}
    </div>
  );
}

export default Events;
